// ---------------------------------------------------------------------------
// B60 — A SIGNED-IN PAGE IS NEVER SHOWN FROM THE BROWSER'S BACK/FORWARD CACHE.
//
// When a document is left by a full page load, the browser may keep it alive,
// whole — DOM, React state, the Redux store — and put it back on Back without
// running a line of this app. A signed-out tab reaches /login by exactly such a
// load (services/api.ts sets `window.location.href`), so Back used to restore
// the signed-in page: the founder saw the Admin dashboard again after another
// browser had ended his session (S3 check 3, 2026-10-06). Measured on dev: no
// request is made on a restore, and the restored page stays up until someone
// clicks. Revocation held — nothing new was served — but what that tab had on
// screen was shown again to whoever pressed Back.
//
// ⚠ `Cache-Control: no-store` on the HTML does NOT prevent it: measured, Chrome
// 148 restored the page with that header too. So the app decides, here:
//
//   pagehide (persisted) on a page rendered for a session → blank it, so the
//     copy the browser keeps shows nothing when it comes back;
//   pageshow (persisted) of a page blanked that way → reload it, which runs
//     the ordinary start-up: AuthGuard asks the server, or sends a tab with no
//     token to /login.
//
// A reload is what every browser did on Back before these caches existed, so no
// page meets anything new. Pages rendered with no session (the marketing site,
// pay links) are left alone and keep the instant Back.
//
// Guard: frontend/tests/signed-out-back.spec.ts — a real Chromium with the cache
// switched back ON (Playwright turns it off by default) and a positive control
// asserting the restore really happened.
// ---------------------------------------------------------------------------

export function installBackForwardCacheGuard(
  renderedForSession: () => boolean,
  win: Window = window,
): void {
  let blanked = false;

  win.addEventListener('pagehide', (event) => {
    if (!(event as PageTransitionEvent).persisted || !renderedForSession()) return;
    blanked = true;
    win.document.documentElement.style.visibility = 'hidden';
  });

  win.addEventListener('pageshow', (event) => {
    if (!(event as PageTransitionEvent).persisted || !blanked) return;
    win.location.reload();
  });
}
